import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import {
    FaPlus, FaEdit, FaTrash, FaGlobe, FaHashtag,
    FaCheckCircle, FaExclamationTriangle, FaBan, FaSearch, FaSync,
    FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import axiosInstance from '../utils/axiosInstance';
import { toast } from 'react-toastify';
import './css/DomainSettings.css';

const DomainSettings = () => {
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingDomain, setEditingDomain] = useState(null);
    const [formData, setFormData] = useState({
        domainUrl: '',
        sequenceNumber: 1,
        status: 'pending' // Default ekledik
    });

    // Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    const fetchDomains = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/domain/all');
            setDomains(res.data);
        } catch (err) {
            toast.error("Domainler yüklenemedi!");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchDomains(); }, []);

    const handleOpenModal = (domain = null) => {
        if (domain) {
            setEditingDomain(domain);
            setFormData({
                domainUrl: domain.domainUrl,
                sequenceNumber: domain.sequenceNumber,
                status: domain.status
            });
        } else {
            // En yüksek sequenceNumber'ı bul
            const maxSequence = domains.length > 0
                ? Math.max(...domains.map(d => d.sequenceNumber || 0))
                : 0;

            setEditingDomain(null);
            setFormData({
                domainUrl: '',
                sequenceNumber: maxSequence + 1, // En büyüğün bir fazlası
                status: 'pending' // Default beklemede
            });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Backend integer beklediği için parseInt yapıyoruz
        const finalData = {
            ...formData,
            sequenceNumber: parseInt(formData.sequenceNumber)
        };

        try {
            if (editingDomain) {
                await axiosInstance.patch(`/domain/update/${editingDomain._id}`, finalData);
                toast.success("Domain güncellendi.");
            } else {
                await axiosInstance.post('/domain/add', finalData);
                toast.success("Yeni domain eklendi.");
            }
            setIsModalOpen(false);
            fetchDomains();
        } catch (err) {
            toast.error(err.response?.data?.error || "İşlem başarısız.");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Bu domaini silmek istediğinize emin misiniz?")) {
            try {
                await axiosInstance.delete(`/domain/delete/${id}`);
                toast.success("Domain silindi.");
                fetchDomains();
            } catch (err) {
                toast.error("Silme işlemi başarısız.");
            }
        }
    };

    // Filtreleme Mantığı
    const sortedDomains = [...domains].sort((a, b) => {
        const priority = { active: 1, blocked: 2, pending: 3 };
        return priority[a.status] - priority[b.status];
    });

    // Filtrelemeyi artık sıralanmış liste üzerinden yapıyoruz
    const filteredDomains = sortedDomains.filter(d => {
        const search = searchTerm.toLowerCase();

        // Durumları Türkçe karşılıklarıyla eşleştirelim
        const statusMap = {
            active: 'aktif',
            pending: 'beklemede',
            blocked: 'engellendi'
        };

        const matchesUrl = d.domainUrl.toLowerCase().includes(search);
        const matchesStatus = statusMap[d.status]?.includes(search);
        const matchesStatusDirect = d.status.toLowerCase().includes(search);

        return matchesUrl || matchesStatus || matchesStatusDirect;
    });


    // Pagination Hesaplamaları
    const totalPages = Math.ceil(filteredDomains.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredDomains.slice(indexOfFirstItem, indexOfLastItem);

    // Arama yapıldığında 1. sayfaya dön
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    return (
        <div className="data-page-container">
            <header className="header-card">
                <div className="header-info">
                    <h2>Domain Yönetimi <span className="count-badge">{domains.length}</span></h2>
                    <p>Sistemdeki aktif ve engellenmiş tüm domainlerin takibi.</p>
                </div>
                <div className="action-group">
                    <button className="btn-modern btn-refresh" onClick={fetchDomains} title="Yenile">
                        <FaSync className={loading ? 'fa-spin' : ''} />
                    </button>
                    <button className="btn-modern btn-add" onClick={() => handleOpenModal()}>
                        <FaPlus /> Yeni Ekle
                    </button>
                </div>
            </header>

            <div className="search-bar-container">
                <FaSearch className="search-icon" />
                <input
                    type="text"
                    placeholder="Domain ara (örn: domain.com)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="table-container">
                <table className="modern-table">
                    <thead>
                        <tr>
                            <th><FaHashtag className="th-icon" /> SIRA</th>
                            <th><FaGlobe className="th-icon" /> DOMAİN URL</th>
                            <th>DURUM</th>
                            <th>SON KONTROL</th>
                            <th style={{ textAlign: 'center' }}>İŞLEMLER</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.length > 0 ? (
                            currentItems.map((item) => (
                                <tr key={item._id} className="table-row">
                                    <td className="wait-time">#{item.sequenceNumber}</td>
                                    <td className="user-name">{item.domainUrl}</td>
                                    <td>
                                        <span className={`status-pill ${item.status}`}>
                                            {item.status === 'active' && <FaCheckCircle />}
                                            {item.status === 'pending' && <FaExclamationTriangle />}
                                            {item.status === 'blocked' && <FaBan />}
                                            {item.status?.toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="date-cell">
                                        {item.lastCheck
                                            ? dayjs(item.lastCheck).format('DD MMM, HH:mm')
                                            : 'Henüz Kontrol Edilmedi'}
                                    </td>
                                    <td className="action-cells">
                                        <button className="btn-action edit" onClick={() => handleOpenModal(item)}>
                                            <FaEdit />
                                        </button>
                                        <button className="btn-action reject" onClick={() => handleDelete(item._id)}>
                                            <FaTrash />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="no-data-cell">Kayıtlı domain bulunamadı.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Navigasyonu */}
            {totalPages > 1 && (
                <div className="pagination-container">
                    <button
                        className="pagi-btn"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(prev => prev - 1)}
                    >
                        <FaChevronLeft />
                    </button>

                    <div className="pagi-info">
                        Sayfa <span>{currentPage}</span> / {totalPages}
                    </div>

                    <button
                        className="pagi-btn"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(prev => prev + 1)}
                    >
                        <FaChevronRight />
                    </button>
                </div>
            )}

            {/* Modal - Aynı Kalıyor */}
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>{editingDomain ? 'Domain Düzenle' : 'Yeni Domain Ekle'}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Domain URL</label>
                                <input
                                    type="text"
                                    value={formData.domainUrl}
                                    onChange={(e) => setFormData({ ...formData, domainUrl: e.target.value })}
                                    placeholder="example.com"
                                    required
                                />
                            </div>

                            {/* Sıralama numarasını yeni eklerken sadece bilgi olarak göster veya salt okunur yap */}
                            <div className="form-group">
                                <label>Sıralama Numarası</label>
                                <input
                                    type="number"
                                    value={formData.sequenceNumber}
                                    readOnly // Yeni eklerken manuel müdahaleyi engellemek için
                                    style={{ opacity: 0.7, cursor: 'not-allowed' }}
                                />
                            </div>

                            {/* SADECE DÜZENLEME MODUNDAYSA DURUM SEÇİMİ GÖRÜNSÜN */}
                            {editingDomain && (
                                <div className="form-group">
                                    <label>Domain Durumu</label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                        className="form-select-modern"
                                    >
                                        <option value="active">Active</option>
                                        <option value="pending">Pending</option>
                                        <option value="blocked">Blocked</option>
                                    </select>
                                </div>
                            )}

                            <div className="modal-actions">
                                <button type="button" className="btn-modal btn-cancel" onClick={() => setIsModalOpen(false)}>İptal</button>
                                <button type="submit" className="btn-modal btn-confirm-reject">
                                    {editingDomain ? 'Güncelle' : 'Sisteme Ekle'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DomainSettings;